import { FormProvider, useForm } from 'react-hook-form';
import { type FC, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Save } from 'lucide-react';
import { capitalize } from 'lodash';
import { z } from 'zod';
import { renderField } from '@/components/inspector/entity-fields/form/field-components';
import { type BackendError, useFormErrorHandler } from '@/hooks/useFormErrorHandler';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useDestination } from '@/hooks/destinations/useDestinations';
import { updateDestination } from '@/services/destination.service';
import { useAccount } from '@/hooks/data/account/useAccount';
import { TenantUserRole } from '@/types/tenantUserRole';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { error as errorLog } from '@/utilities/log';
import { Button } from '@/components/ui/button';
import { QueryKey } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

type DestinationFieldData = {
  id: string;
  name?: string;
  slug?: string;
  description?: string;
};

export const DestinationFields: FC<{ destinationId: string }> = ({ destinationId }) => {
  const queryClient = useQueryClient();
  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts');
  const tValidation = useTranslations('validation');

  const { toast } = useToast();
  const {
    destination: fieldData,
    destinationIsFetching,
    destinationIsPending,
  } = useDestination(destinationId) as {
    destination: DestinationFieldData;
    destinationIsFetching: boolean;
    destinationIsPending: boolean;
  };
  const {
    profile: {
      user: { role: userRole },
    },
  } = useAccount();

  // Create a dynamic form schema based on the fieldData (empty schema while it loads)
  const createFormSchema = (data?: DestinationFieldData) => {
    const schema: Record<string, z.ZodType<any>> = {};

    Object.keys(data ?? {}).forEach((key) => {
      switch (key) {
        case 'name':
          schema[key] = z
            .string()
            .min(1, { message: tValidation('fieldRequired', { field: `${capitalize(key)} field` }) })
            .max(255, { message: tValidation('fieldMaxLength', { field: `${capitalize(key)} field`, max: 255 }) });
          break;
        case 'description':
          schema[key] = z
            .string({
              error: (issue) => {
                if (issue.input === undefined) {
                  return tValidation('fieldMustBeString', { field: tValidation('fields.descriptionField') });
                }
              },
            })
            .nullable();
          break;
        case 'slug':
          schema[key] = z
            .string()
            .min(1, { message: tValidation('fieldRequired', { field: `${capitalize(key)} field` }) })
            .max(255, { message: tValidation('fieldMaxLength', { field: `${capitalize(key)} field`, max: 255 }) });
          break;
        default:
          break;
      }
    });

    return z.object(schema);
  };

  const formSchema = createFormSchema(fieldData);

  // Dynamic default values based on the fieldData
  const createDefaultValues = (data: DestinationFieldData) => {
    const defaultValues: Partial<Record<keyof DestinationFieldData, any>> = {};

    if (data) {
      Object.keys(data).forEach((key) => {
        defaultValues[key as keyof DestinationFieldData] = data[key as keyof DestinationFieldData];
      });

      return defaultValues;
    }
  };

  const form = useForm({
    // formSchema falls back to an empty schema while the destination is loading —
    // zodResolver throws if given undefined.
    resolver: zodResolver(formSchema),
    defaultValues: fieldData && createDefaultValues(fieldData),
  });

  const { handleError } = useFormErrorHandler(form.setError, form.getValues);

  // Reset form values when fieldData changes
  useEffect(() => {
    form.reset(createDefaultValues(fieldData));
  }, [fieldData, form.reset]);

  const updateDestinationWithAuth = useAuthenticatedQueryFn(updateDestination);

  const updateDestinationMutation = useMutation({
    mutationFn: updateDestinationWithAuth,
    onSuccess: async () => {
      toast({
        title: tToasts('updateDestinationMutation.title'),
        description: tToasts('updateDestinationMutation.description'),
      });

      await queryClient.invalidateQueries({ queryKey: [QueryKey.DestinationList] });
    },
    onError: (err: BackendError) => {
      handleError(err);
      errorLog(err);
    },
  });

  const isLoading = updateDestinationMutation.isPending;
  const disabled = destinationIsFetching || isLoading || userRole !== TenantUserRole.Owner;

  function onSubmit(values: z.infer<ReturnType<typeof createFormSchema>>) {
    if (disabled) {
      return;
    }

    updateDestinationMutation.mutate({
      id: fieldData.id,
      body: {
        ...values,
      },
    });
  }

  return destinationIsPending ? (
    <div className="flex flex-col space-y-6 p-4">
      {Array.from(Array(3)).map((_, index) => (
        <>
          <div key={`skeleton-${index}`} className="flex flex-col space-y-3">
            <Skeleton className={cn('h-5 w-1/3', { 'w-2/5': index === 0 })} />
            <Skeleton className="h-8 w-full" />
            <Skeleton className={cn('h-5 w-1/3', { 'w-2/3': index === 1 })} />
          </div>
          {index !== 2 && <Separator className="block" />}
        </>
      ))}
    </div>
  ) : (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="relative flex flex-col space-y-6 p-4">
        {Object.keys(fieldData).map((key) => {
          const validation = (formSchema.shape as Record<string, z.ZodType<any>>)[key];

          switch (key) {
            case 'name':
              return renderField(form, {
                'name': 'Destination Name',
                'type': 'string',
                'slug': key,
                'disabled': disabled,
                validation,
                'data-cy': 'destination-name-field',
                'isLoading': destinationIsFetching,
              });
            case 'slug':
              return renderField(form, {
                'name': 'Destination Slug',
                'type': 'string',
                'slug': key,
                'disabled': disabled,
                validation,
                'data-cy': 'destination-slug-field',
                'isLoading': destinationIsFetching,
              });
            case 'description':
              return renderField(form, {
                'name': 'Destination Description',
                'type': 'text',
                'slug': key,
                'disabled': disabled,
                validation,
                'data-cy': 'destination-description-field',
                'isLoading': destinationIsFetching,
              });
            default:
              return null;
          }
        })}

        <Button
          disabled={disabled}
          type="submit"
          variant="navigation-action-primary"
          className="w-full justify-center focus-visible:ring-offset-ui-panel"
          data-cy="destination-save-button"
          iconClassName={cn('icon-4!', { 'animate-spin': isLoading })}
          icon={isLoading ? Loader2 : Save}
        >
          {isLoading ? tCommon('saving') : tCommon('save')}
        </Button>
      </form>
    </FormProvider>
  );
};
